import React, { useState, useEffect } from 'react';
import { Typography, Table, Button, Space, Tooltip, message, Tag, Modal } from 'antd';
import { CopyOutlined, DownloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';
import BatchSequenceResult from './BatchSequenceResult';

const { Text } = Typography;

// 氨基酸颜色映射 (基于物理化学性质)
const aminoAcidColors: Record<string, string> = {
  // 疏水性 (黄色)
  'A': '#FFD700', 'I': '#FFD700', 'L': '#FFD700', 'M': '#FFD700', 'F': '#FFD700', 'V': '#FFD700', 'P': '#FFD700', 'G': '#FFD700',
  // 极性 (绿色)
  'N': '#00FF00', 'Q': '#00FF00', 'S': '#00FF00', 'T': '#00FF00',
  // 酸性 (红色)
  'D': '#FF6347', 'E': '#FF6347',
  // 碱性 (蓝色)
  'R': '#6495ED', 'H': '#6495ED', 'K': '#6495ED',
  // 特殊 (紫色)
  'C': '#9370DB', 'W': '#9370DB', 'Y': '#9370DB',
  // 终止密码子 (灰色)
  '*': '#808080',
};

// 查找开放阅读框 (ORF)
const findORFs = (sequence: string) => {
  const orfs: string[] = [];
  let currentOrf = '';
  let inOrf = false;
  
  for (let i = 0; i < sequence.length; i++) {
    const aa = sequence[i];
    
    if (aa === 'M') {
      // 起始密码子
      inOrf = true;
      currentOrf = 'M';
    } else if (aa === '*') {
      // 终止密码子
      if (inOrf && currentOrf.length > 0) {
        orfs.push(currentOrf);
        currentOrf = '';
        inOrf = false;
      }
    } else if (inOrf) {
      // 在ORF内部
      currentOrf += aa;
    }
  }
  
  // 检查最后一个可能未终止的ORF
  if (inOrf && currentOrf.length > 0) {
    orfs.push(currentOrf);
  }
  
  return orfs;
};

// 六框翻译结果组件
const SixFrameTranslationResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults = false,
  resultIndex,
}) => {
  // 检查是否为批量结果
  const isBatchResult = (data?.items && Array.isArray(data.items) && data.count !== undefined) ||
                        (data?.results && Array.isArray(data.results) && data.total_sequences !== undefined);
  
  if (isBatchResult) {
    return (
      <BatchSequenceResult
        data={data}
        title={title || '批量六框翻译结果'}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        resultIndex={resultIndex}
      />
    );
  }

  // 原有的单序列翻译逻辑保持不变
  const [originalSequence, setOriginalSequence] = useState('');
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [sequenceType, setSequenceType] = useState('');
  const [originalId, setOriginalId] = useState('');

  // 初始化数据
  useEffect(() => {
    if (data) {
      setOriginalSequence(data.original_sequence || '');
      setTranslations(data.translations || {});
      setSequenceType(data.from_type || '');
      setOriginalId(data.originalId || '>序列');
    }
  }, [data]);

  // 复制序列到剪贴板
  const copySequence = (sequence: string, frame: string) => {
    try {
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = sequence;
      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        message.success(`已复制阅读框 ${frame} 的序列`);
      } else {
        message.error('复制失败，请手动复制');
      }
    } catch (err) {
      console.error('复制失败:', err);
      message.error('复制失败，请手动复制');
    }
  };

  // 复制为FASTA格式
  const copyAsFasta = (frame: string) => {
    try {
      const sequence = translations[frame];
      if (!sequence) return;
      
      const fastaHeader = `${originalId}_frame${frame}`;
      const fastaContent = `${fastaHeader}\n${sequence}`;
      
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = fastaContent;
      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        message.success(`已复制阅读框 ${frame} 的FASTA序列`);
      } else {
        message.error('复制失败，请手动复制');
      }
    } catch (err) {
      console.error('复制失败:', err);
      message.error('复制失败，请手动复制');
    }
  };

  // 下载所有翻译结果为FASTA文件
  const downloadAllAsFasta = () => {
    try {
      const fastaContent = Object.entries(translations).map(([frame, sequence]) => {
        const fastaHeader = `${originalId}_frame${frame}`;
        return `${fastaHeader}\n${sequence}`;
      }).join('\n');
      
      const blob = new Blob([fastaContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'six_frame_translation.fasta';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      message.success('已下载所有翻译结果为FASTA文件');
    } catch (err) {
      console.error('下载失败:', err);
      message.error('下载失败');
    }
  };

  // 显示完整序列对话框
  const showFullSequence = (sequence: string) => {
    Modal.info({
      title: '完整序列',
      width: 800,
      content: (
        <div style={{ 
          maxHeight: '500px', 
          overflow: 'auto',
          fontFamily: 'monospace', 
          whiteSpace: 'pre-wrap', 
          wordBreak: 'break-all',
          backgroundColor: '#f9f9f9',
          padding: '8px',
          borderRadius: '4px',
        }}>
          {sequence.split('').map((aa, idx) => {
            const color = aminoAcidColors[aa] || '#000000';
            
            return (
              <span
                key={idx}
                style={{
                  color: aa === '*' ? '#FFFFFF' : '#000000',
                  backgroundColor: color,
                  padding: '2px 0',
                  margin: '0 1px',
                  fontWeight: aa === 'M' || aa === '*' ? 'bold' : 'normal',
                  fontSize: '14px',
                  display: 'inline-block',
                  width: '16px',
                  textAlign: 'center',
                  borderRadius: '2px',
                }}
              >
                {aa}
              </span>
            );
          })}
        </div>
      ),
      maskClosable: true,
    });
  };

  // 渲染彩色氨基酸序列
  const renderColoredSequence = (sequence: string) => {
    // 为了避免渲染太多元素导致性能问题，只显示前100个氨基酸
    const displaySequence = sequence.length > 100 ? sequence.substring(0, 100) + '...' : sequence;
    
    return (
      <div style={{ 
        fontFamily: 'monospace', 
        whiteSpace: 'pre-wrap', 
        wordBreak: 'break-all',
        overflowX: 'auto',
        maxWidth: '600px'
      }}>
        {displaySequence.split('').map((aa, index) => {
          const color = aminoAcidColors[aa] || '#000000';
          
          return (
            <Tooltip key={index} title={`位置 ${index + 1}: ${aa}`}>
              <Text
                style={{
                  color: aa === '*' ? '#FFFFFF' : '#000000',
                  backgroundColor: color,
                  padding: '2px 0',
                  margin: '0 1px',
                  fontWeight: aa === 'M' || aa === '*' ? 'bold' : 'normal',
                  fontSize: '14px',
                  display: 'inline-block',
                  width: '16px',
                  textAlign: 'center',
                  borderRadius: '2px',
                }}
              >
                {aa}
              </Text>
            </Tooltip>
          );
        })}
        {sequence.length > 100 && (
          <Tooltip title={`显示全部 ${sequence.length} 个氨基酸`}>
            <Button 
              type="link" 
              size="small" 
              onClick={() => showFullSequence(sequence)}
            >
              查看全部
            </Button>
          </Tooltip>
        )}
      </div>
    );
  };

  // 表格数据
  const tableData = Object.entries(translations).map(([frame, sequence]) => {
    // 计算开放阅读框 (ORF)
    const orfs = findORFs(sequence);
    const longestOrf = orfs.length > 0 ? Math.max(...orfs.map(orf => orf.length)) : 0;
    
    return {
      key: frame,
      frame,
      direction: frame.startsWith('+') ? '正向' : '反向',
      frameNumber: frame.replace('+', '').replace('-', ''),
      sequence,
      length: sequence.length,
      orfs: orfs.length,
      longestOrf,
    };
  });

  // 表格列定义
  const columns = [
    {
      title: '阅读框',
      dataIndex: 'frame',
      key: 'frame',
      width: 80,
      render: (text: string) => (
        <Tag color={text.startsWith('+') ? 'blue' : 'orange'}>
          {text}
        </Tag>
      ),
    },
    {
      title: '方向',
      dataIndex: 'direction',
      key: 'direction',
      width: 80,
    },
    {
      title: '序列长度',
      dataIndex: 'length',
      key: 'length',
      width: 100,
      sorter: (a: any, b: any) => a.length - b.length,
    },
    {
      title: 'ORF数量',
      dataIndex: 'orfs',
      key: 'orfs',
      width: 100,
      sorter: (a: any, b: any) => a.orfs - b.orfs,
    },
    {
      title: '最长ORF',
      dataIndex: 'longestOrf',
      key: 'longestOrf',
      width: 100,
      sorter: (a: any, b: any) => a.longestOrf - b.longestOrf,
    },
    {
      title: '翻译序列',
      dataIndex: 'sequence',
      key: 'coloredSequence',
      render: (sequence: string, record: any) => renderColoredSequence(sequence),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: unknown, record: { sequence: string, frame: string }) => (
        <Space>
          <Button 
            size="small" 
            icon={<CopyOutlined />} 
            onClick={() => copySequence(record.sequence, record.frame)}
          >
            复制
          </Button>
          <Button 
            size="small" 
            icon={<FileTextOutlined />} 
            onClick={() => copyAsFasta(record.frame)}
          >
            FASTA
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <BaseResultDisplay
      title={title || "核酸序列六框翻译结果"}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
    >
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text type="secondary">原始序列类型: {sequenceType.toUpperCase()}</Text>
            <br />
            <Text type="secondary">原始序列长度: {originalSequence.length} 核苷酸</Text>
          </div>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={downloadAllAsFasta}
          >
            下载所有FASTA
          </Button>
        </div>
        
        <Table 
          columns={columns} 
          dataSource={tableData}
          pagination={false}
          size="middle"
          bordered
          scroll={{ x: 'max-content' }}
        />
        
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            说明: 颜色代表氨基酸的物理化学性质。黄色=疏水性, 绿色=极性, 红色=酸性, 蓝色=碱性, 紫色=特殊, 灰色=终止密码子(*)。
            M为起始密码子, *为终止密码子。ORF是从起始密码子(M)到终止密码子(*)的开放阅读框。
          </Text>
        </div>
      </div>
    </BaseResultDisplay>
  );
};

export default SixFrameTranslationResult;